/*
 * Copyright © 2020-2024 Ocean Galaxy Inc. All Rights Reserved.
 * @Description:
 * @LastEditors: yongqiang
 * @LastEditTime: 2024-01-11 11:41:30
 */
import React, { useEffect, useState } from 'react'
import { InputNumber, Switch, Popover, Tooltip } from 'antd'
import classnames from 'classnames'
import sensors from '../../utils/sensors'
import {
  AlignCenterOutlined,
  AlignLeftOutlined,
  AlignRightOutlined,
  CloseCircleOutlined
} from '@ant-design/icons'
import type { JigsawData, TableConfig } from './types'

interface TableSettingProps {
  jigsawData: JigsawData
  onChange: (config: TableConfig) => void
}

const TableSetting = ({ jigsawData, onChange }: TableSettingProps) => {
  const { tableConfig } = jigsawData
  const [popupVisible, setPopupVisible] = useState(
    !JSON.parse(
      localStorage.getItem('jigsaw_new_feat_table_popuped') || 'false'
    )
  )

  const Content = () => {
    return (
      <div style={{ position: 'relative', paddingRight: 40 }}>
        新增「表格」功能
        <br />
        开启后可在拼图中输入表格内容{' '}
        <CloseCircleOutlined
          style={{ position: 'absolute', top: 0, right: 0 }}
          onClick={() => {
            setPopupVisible(false)
            localStorage.setItem('jigsaw_new_feat_table_popuped', 'true')
            sensors.track('web_workgroup_jigsaw_click', {
              clickItem: 'new_feat_table_popup_close'
            })
          }}
        />
      </div>
    )
  }

  const handleRowsChange = (value: number | null) => {
    if (value === null) return
    const newData = [...tableConfig.data]
    if (value > tableConfig.rows) {
      //增加行
      newData.push(new Array(tableConfig.cols).fill(''))
    } else {
      //减少行
      if (newData.length > 1) {
        newData.pop()
      }
    }
    onChange({
      ...tableConfig,
      rows: value,
      data: newData
    })
  }

  const handleColsChange = (value: number | null) => {
    if (value === null) return
    const newData = [...tableConfig.data]
    newData.forEach((rowData) => {
      if (value > tableConfig.cols) {
        //增加列
        rowData.push('')
      } else {
        //减少列
        if (rowData.length > 1) {
          rowData.pop()
        }
      }
    })
    onChange({
      ...tableConfig,
      cols: value,
      data: newData
    })
    console.log(newData)
  }

  return (
    <div className="table-setting">
      <div className="table-switch">
        开启表格
        <Popover
          content={<Content />}
          open={popupVisible}
          getPopupContainer={(triggerNode) =>
            triggerNode.parentNode as HTMLElement
          }
          classNames={{ root: 'jigsaw-history-popover' }}
        >
          <Switch
            size="small"
            style={{ marginLeft: 5 }}
            checked={tableConfig.tableSwitch}
            onChange={(checked) => {
              sensors.track('web_workgroup_jigsaw_click', {
                clickItem: 'tableSwitch',
                open: checked
              })
              onChange({
                ...tableConfig,
                tableSwitch: checked
              })
            }}
          />
        </Popover>
      </div>
      {tableConfig.tableSwitch && (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <InputNumber
            min={1}
            max={9}
            style={{ width: '60px', marginRight: '4px' }}
            value={tableConfig.rows}
            onChange={handleRowsChange}
          />
          行
          <InputNumber
            min={1}
            max={9}
            style={{
              width: '60px',
              marginRight: '4px',
              marginLeft: '10px'
            }}
            value={tableConfig.cols}
            onChange={handleColsChange}
          />
          列
          <div className="cell-align">
            <Tooltip title="左对齐">
              <span
                onClick={() => onChange({ ...tableConfig, cellAlign: 'left' })}
              >
                <AlignLeftOutlined
                  className={classnames('align-left-btn', {
                    'align-btn-active': tableConfig.cellAlign === 'left'
                  })}
                />
              </span>
            </Tooltip>
            <Tooltip title="居中对齐">
              <span
                onClick={() =>
                  onChange({ ...tableConfig, cellAlign: 'center' })
                }
              >
                <AlignCenterOutlined
                  className={classnames('align-center-btn', {
                    'align-btn-active': tableConfig.cellAlign === 'center'
                  })}
                />
              </span>
            </Tooltip>
            <Tooltip title="右对齐">
              <span
                onClick={() => onChange({ ...tableConfig, cellAlign: 'right' })}
              >
                <AlignRightOutlined
                  className={classnames('align-right-btn', {
                    'align-btn-active': tableConfig.cellAlign === 'right'
                  })}
                />
              </span>
            </Tooltip>
          </div>
        </div>
      )}
    </div>
  )
}

export default TableSetting
